import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { offDutyStatuses, onDutyStatuses, type IState } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type DutySinceCycleRestConditions = {
    avail: number | undefined;
    /** Driver is on-duty (ON/D/YM) */
    isOnDuty: boolean;
    /**
     * Driver is off-duty AND has pending qualifying rest that will reset dutyCountSince24HourRestPeriod:
     * off-duty status, cycleRestMinimum configured, counting not paused,
     * cycleConsecutiveOffCount < cycleRestMinimum, and dutyCountSince24HourRestPeriod > 0.
     */
    canQualify: boolean;
    cycleRestMinimum: number | undefined;
    cycleConsecutiveOffCount: number;
};

export class DutySinceCycleRestCalculator extends AvailabilityCalculator {
    private conditions(state: IState): DutySinceCycleRestConditions {
        const avail = discreteAvailabilityFunctions.DutySinceCycleRest(state, false);
        const isOnDuty = onDutyStatuses.indexOf(state.current?.status) !== -1;
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const cycleRestMinimum = state.config.cycle?.cycleRestMinimum;
        const cycleConsecutiveOffCount = state.cycle.cycleConsecutiveOffCount ?? 0;
        const canQualify = isOffDuty
            && cycleRestMinimum !== undefined
            && !state.cycle.pauseCountingCycleOff
            && cycleConsecutiveOffCount < cycleRestMinimum
            && (state.cycle.dutyCountSince24HourRestPeriod ?? 0) > 0;
            
        return { avail, isOnDuty, canQualify, cycleRestMinimum, cycleConsecutiveOffCount };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        const c = this.conditions(state);
        return c.isOnDuty;
    }

    exhaustion(state: IState): Date | undefined {
        const { avail, isOnDuty } = this.conditions(state);
        if (avail === undefined || !isOnDuty) return undefined;
        
        // Guard avail > 0: raw value may be ≤ 0 when already in violation — no future
        // zero-crossing exists in that case.
        if (avail > 0) {
            return new Date(state.currentDate.getTime() + avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        if (c.isOnDuty) {
            // Driver on-duty: availability drains at rate −1. There are no genuine inflections.
            // Exhaustion is handled by the base class.
            return {};
        }

        if (c.canQualify) {
            // Driver off-duty, break qualifying.
            // stable break start anchor = currentDate − cycleConsecutiveOffCount
            const breakStartTime = state.currentDate.getTime() - c.cycleConsecutiveOffCount;
            const cycleRestQualifyingDate = new Date(breakStartTime + c.cycleRestMinimum!);
            return {
                cycleRestQualifying: { factor: () => cycleRestQualifyingDate, label: "break qualifies" },
            };
        }

        // Driver off-duty but qualifying conditions not met.
        return {};
    }
}

export const extractDutySinceCycleRestConditions = (state: IState): DutySinceCycleRestConditions => {
    return new DutySinceCycleRestCalculator().extractConditions(state) as DutySinceCycleRestConditions;
};

export const computeDutySinceCycleRestResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new DutySinceCycleRestCalculator().compute(state);
};
